import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {useQueryClient} from '@tanstack/react-query';
import {isAxiosError} from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  CreateEventGroupDialog,
  Event,
  EventGroupType,
  EventHoverCard,
  EventStatus,
  formatEventStatus,
  useConfirmEvents,
  useCreateEventGroup,
} from '@entities/event';
import {
  getExternalParticipantUuid,
  Participant,
  ParticipantSelect,
  unbindExternalParticipant,
  useBindExternalParticipant,
  useGetExternalParticipants,
} from '@entities/participant';
import {
  ApiSportsMappingField,
  ApiSportsMatchField,
  getSportServiceKey,
  SeasonSelect,
  SPORT_CATEGORY_SLUGS,
  useGetLeagueSeasons,
  useGetSportMatch,
} from '@entities/sportMatch';
import {
  QUERY__TOPIC,
  updateTopicParticipants,
  useGetTopic,
  useGetTopicEvents,
  useRefreshTopicEvents,
  useUpdateTopic,
} from '@entities/topic';
import {getBackendAssetPath, getErrorMessageFromApiError} from '@shared/api';
import {
  CardBox,
  centerAlignmentColumnProps,
  ClassicMuiNavTabs,
  CurrencyValue,
  MrTable,
  openConfirmation,
  rightAlignmentColumnProps,
} from '@shared/components';
import {
  DATETIME_FORMAT,
  entityTableCommonProps,
  ROUTE__EVENT_DETAILS,
  ROUTE__PARTICIPANT_CREATE,
  ROUTE__PARTICIPANT_DETAILS,
  ROUTE__TOPIC_CHAT,
  ROUTE__TOPIC_DETAILS,
  ROUTE__TOPIC_POOLS,
} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {formatInTz, toPickerDateInTz} from '@shared/utils';
import {BaseWidgetLayout, Header} from '@widgets/common';
import {PoolsTable} from '@widgets/pool/PoolsTable';
import {EditTopicForm} from '@widgets/topic';
import {ChatTab} from './ChatTab';

import {PaginationState} from '@/shared/types';
import {COLOR__WHITE} from '@/theme/colors';

export const EditTopicPage = () => {
  const {topic_id} = useParams<{topic_id: string}>();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: confirmEvents, isPending: isConfirmingEvents} =
    useConfirmEvents();
  const {mutateAsync: createEventGroup, isPending: isCreatingGroup} =
    useCreateEventGroup();
  const {mutateAsync: updateTopic} = useUpdateTopic();
  const {mutateAsync: refreshTopicEvents, isPending: isRefreshingTopic} =
    useRefreshTopicEvents();
  const [selectedEventSlugs, setSelectedEventSlugs] = useState<Set<string>>(
    new Set(),
  );
  // Subset of `selectedEventSlugs` that are already published. Tracked
  // separately so the Publish button can be disabled whenever a published
  // event is part of the selection, even across pages.
  const [selectedPublishedEventSlugs, setSelectedPublishedEventSlugs] =
    useState<Set<string>>(new Set());
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [isPublishingEvents, setIsPublishingEvents] = useState(false);
  const [autoPublishDraft, setAutoPublishDraft] = useState<{
    topicId: string;
    enabled: boolean;
  } | null>(null);
  const handleAutoPublishChange = useCallback(
    (enabled: boolean) => {
      setAutoPublishDraft({topicId: topic_id ?? '', enabled});
    },
    [topic_id],
  );

  const {urlState, setUrl} = useUrlState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const {data, isPending, error} = useGetTopic(topic_id ?? '', {
    enabled: !!topic_id,
  });

  const categorySlug = data?.category?.id?.toLowerCase();
  // Picks the sport backend: the category slug, or the subcategory slug for
  // esports disciplines (e.g. dota2) that have their own service.
  const sportKey = getSportServiceKey(
    categorySlug,
    data?.subCategories?.[0]?.slug,
  );
  const isSportCategory = SPORT_CATEGORY_SLUGS.includes(sportKey ?? '');
  // APISports mapping endpoints currently exist only for the football backend.
  const isApiSportsMappingSupported = sportKey === 'football';

  const {data: sportMatch, isPending: isSportPending} = useGetSportMatch(
    sportKey,
    data?.id,
  );

  // The matches mapping search requires a season, and the Season dropdown that
  // picks it lives in the Competition block - so the selection is held here and
  // shared with the APISports match field above it.
  const sportLeagueId = isApiSportsMappingSupported
    ? sportMatch?.league.id
    : undefined;
  const {data: leagueSeasons = []} = useGetLeagueSeasons(
    sportKey,
    sportLeagueId,
  );
  const [selectedSeason, setSelectedSeason] = useState<string | null>(null);

  // Default to the season the match falls into, so the match dropdown works
  // without the user having to pick one first; the most recent season is the
  // fallback when no season covers the kick-off date.
  const defaultSeason = useMemo(() => {
    if (leagueSeasons.length === 0) return null;
    const matchDay = sportMatch?.lsports.start_date?.slice(0, 10);
    const covering = matchDay
      ? leagueSeasons.find(s => s.start <= matchDay && matchDay <= s.end)
      : undefined;
    const [mostRecent] = [...leagueSeasons].sort((a, b) =>
      b.start.localeCompare(a.start),
    );
    return (covering ?? mostRecent)?.season ?? null;
  }, [leagueSeasons, sportMatch?.lsports.start_date]);

  useEffect(() => {
    if (!selectedSeason && defaultSeason) {
      setSelectedSeason(defaultSeason);
    }
  }, [defaultSeason, selectedSeason]);

  const sortedSportParticipants = useMemo(() => {
    if (!sportMatch) return [];
    return [...sportMatch.participants]
      .filter(p => p.lsports)
      .sort((a, b) => a.lsports!.position - b.lsports!.position);
  }, [sportMatch]);

  const ext1Ids = useMemo(() => {
    const p = sortedSportParticipants[0];
    if (!p) return [];
    return [
      p.lsports ? `lsports:${p.lsports.id}` : null,
      p.id ? `lsports:${p.id}` : null,
    ].filter((id): id is string => !!id);
  }, [sortedSportParticipants]);

  const ext2Ids = useMemo(() => {
    const p = sortedSportParticipants[1];
    if (!p) return [];
    return [
      p.lsports ? `lsports:${p.lsports.id}` : null,
      p.id ? `lsports:${p.id}` : null,
    ].filter((id): id is string => !!id);
  }, [sortedSportParticipants]);

  const {data: externalParticipant1Data} = useGetExternalParticipants(ext1Ids);
  const {data: externalParticipant2Data} = useGetExternalParticipants(ext2Ids);

  const [participant1, setParticipant1] = useState<Participant | null>(null);
  const [participant2, setParticipant2] = useState<Participant | null>(null);
  const initialParticipant1Ref = useRef<Participant | null>(null);
  const initialParticipant2Ref = useRef<Participant | null>(null);

  useEffect(() => {
    const p = externalParticipant1Data?.[0] ?? null;
    setParticipant1(p);
    initialParticipant1Ref.current = p;
  }, [externalParticipant1Data]);

  useEffect(() => {
    const p = externalParticipant2Data?.[0] ?? null;
    setParticipant2(p);
    initialParticipant2Ref.current = p;
  }, [externalParticipant2Data]);

  const hasParticipantsChanged =
    participant1?.participantId !==
      initialParticipant1Ref.current?.participantId ||
    participant2?.participantId !==
      initialParticipant2Ref.current?.participantId;

  const {mutateAsync: bindExternalParticipant, isPending: isLinking} =
    useBindExternalParticipant();
  const queryClient = useQueryClient();

  const handleRefreshTopic = async () => {
    if (!topic_id || isRefreshingTopic) return;

    try {
      await refreshTopicEvents(topic_id);
      enqueueSnackbar('Topic data updated successfully', {variant: 'success'});
    } catch (err) {
      const message = isAxiosError(err) ? getErrorMessageFromApiError(err) : '';
      enqueueSnackbar(`Could not update topic data. ${message}`, {
        variant: 'error',
      });
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  const {
    data: eventsData,
    isPending: isEventsPending,
    error: eventsError,
  } = useGetTopicEvents(
    {
      topicId: topic_id!,
      page: urlState.pageIndex + 1,
      pageSize: urlState.pageSize,
    },
    {
      enabled: !!topic_id,
    },
  );

  const currentPageEventSlugs = useMemo(() => {
    return (eventsData?.items ?? []).map(event => event.eventId);
  }, [eventsData?.items]);

  const selectedCurrentPageCount = useMemo(() => {
    return currentPageEventSlugs.filter(eventSlug =>
      selectedEventSlugs.has(eventSlug),
    ).length;
  }, [currentPageEventSlugs, selectedEventSlugs]);

  const areAllCurrentPageEventsSelected =
    currentPageEventSlugs.length > 0 &&
    selectedCurrentPageCount === currentPageEventSlugs.length;

  const areSomeCurrentPageEventsSelected =
    selectedCurrentPageCount > 0 && !areAllCurrentPageEventsSelected;

  const toggleCurrentPageSelection = useCallback(
    (isSelected: boolean) => {
      const items = eventsData?.items ?? [];

      setSelectedEventSlugs(prev => {
        const next = new Set(prev);

        for (const event of items) {
          if (isSelected) {
            next.add(event.eventId);
          } else {
            next.delete(event.eventId);
          }
        }

        return next;
      });

      setSelectedPublishedEventSlugs(prev => {
        const next = new Set(prev);

        for (const event of items) {
          if (event.status === EventStatus.null) {
            continue;
          }
          if (isSelected) {
            next.add(event.eventId);
          } else {
            next.delete(event.eventId);
          }
        }

        return next;
      });
    },
    [eventsData?.items],
  );

  const toggleEventSelection = useCallback(
    (eventSlug: string, isPublished: boolean, isSelected: boolean) => {
      setSelectedEventSlugs(prev => {
        const next = new Set(prev);

        if (isSelected) {
          next.add(eventSlug);
        } else {
          next.delete(eventSlug);
        }

        return next;
      });

      if (isPublished) {
        setSelectedPublishedEventSlugs(prev => {
          const next = new Set(prev);

          if (isSelected) {
            next.add(eventSlug);
          } else {
            next.delete(eventSlug);
          }

          return next;
        });
      }
    },
    [],
  );

  const handlePublishSelectedEvents = async () => {
    if (selectedEventSlugs.size === 0 || !data || isPublishingEvents) {
      return;
    }

    setIsPublishingEvents(true);
    const autoPublishEnabled =
      autoPublishDraft && autoPublishDraft.topicId === topic_id
        ? autoPublishDraft.enabled
        : data.autoPublishEnabled;
    let shouldEnableAutoPublish =
      autoPublishEnabled && !data.autoPublishEnabled;

    try {
      if (!autoPublishEnabled) {
        try {
          await openConfirmation({
            title: 'Enable topic auto publish?',
            text: 'Auto publish is disabled for this topic. Enable it before publishing the selected events?',
            okButtonText: 'Yes',
            cancelButtonText: 'No',
          });
          shouldEnableAutoPublish = true;
        } catch {
          // "No" (or dismissing the dialog): publish without changing the topic
        }
      }

      if (shouldEnableAutoPublish) {
        await updateTopic({
          topicId: data.topicId,
          nameTrans: data.nameTranslations,
          shortName: data.shortName,
          logoUrl: getBackendAssetPath(data.logoUrl),
          bannerUrl: getBackendAssetPath(data.bannerUrl),
          streamUrl: data.streamUrl ?? null,
          subCategorySlugs: (data.subCategories ?? []).map(
            subCategory => subCategory.slug ?? subCategory.id,
          ),
          // updateTopic expects picker ("wall clock") dates, like the form
          startDate: toPickerDateInTz(data.startDate),
          endDate: toPickerDateInTz(data.endDate ?? data.startDate),
          isLive: data.isLive,
          isFavorite: data.isFavorite ?? false,
          geoSlugs: data.geoSlugs,
          autoPublishEnabled: true,
        });
      }

      await confirmEvents({
        eventSlugs: [...selectedEventSlugs],
        confirm: true,
      });
      setSelectedEventSlugs(new Set());
      setSelectedPublishedEventSlugs(new Set());
      enqueueSnackbar('Events published successfully', {
        variant: 'success',
      });
    } catch (err) {
      const errorMessage = isAxiosError(err)
        ? getErrorMessageFromApiError(err)
        : '';

      enqueueSnackbar(`Could not publish events. ${errorMessage}`, {
        variant: 'error',
      });
    } finally {
      setIsPublishingEvents(false);
    }
  };

  const handleCreateGroup = async (groupType: EventGroupType) => {
    if (selectedEventSlugs.size === 0) {
      return;
    }

    try {
      await createEventGroup({
        eventSlugs: [...selectedEventSlugs],
        groupType,
      });
      setIsGroupDialogOpen(false);
      setSelectedEventSlugs(new Set());
      setSelectedPublishedEventSlugs(new Set());
      enqueueSnackbar('Group created successfully', {
        variant: 'success',
      });
    } catch (err) {
      const errorMessage = isAxiosError(err)
        ? getErrorMessageFromApiError(err)
        : '';

      enqueueSnackbar(`Could not create group. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  const topicEventsColumns = useMemo<MRT_ColumnDef<Event>[]>(() => {
    return [
      {
        id: 'publishSelection',
        header: '',
        Header: () => {
          return (
            <Checkbox
              checked={
                currentPageEventSlugs.length > 0 &&
                areAllCurrentPageEventsSelected
              }
              indeterminate={areSomeCurrentPageEventsSelected}
              disabled={currentPageEventSlugs.length === 0}
              onChange={(_, checked) => {
                toggleCurrentPageSelection(checked);
              }}
            />
          );
        },
        Cell: ({row}) => {
          const eventSlug = row.original.eventId;
          const isPublished = row.original.status !== EventStatus.null;

          return (
            <Checkbox
              checked={selectedEventSlugs.has(eventSlug)}
              onChange={(_, checked) => {
                toggleEventSelection(eventSlug, isPublished, checked);
              }}
            />
          );
        },
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: false,
        ...centerAlignmentColumnProps,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 200,
        Cell: ({row}) => {
          const {eventId, name} = row.original;

          return (
            <EventHoverCard eventId={eventId}>
              <Link
                component={RouterLink}
                to={ROUTE__EVENT_DETAILS.replace(':event_id', eventId)}
              >
                {name}
              </Link>
            </EventHoverCard>
          );
        },
      },
      {
        accessorKey: 'eventId',
        header: 'Slug',
        size: 220,
        enableSorting: false,
      },
      {
        accessorKey: 'groupId',
        header: 'Group',
        size: 120,
        enableSorting: false,
        Cell: ({row}) => {
          const groupId = row.original.groupId;

          if (!groupId) {
            return '-';
          }

          return (
            <Tooltip title={`${groupId} (click to copy)`}>
              <Link
                component="button"
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(groupId);
                  enqueueSnackbar('Group ID copied', {variant: 'success'});
                }}
              >
                {groupId.slice(0, 6)}
              </Link>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 140,
        Cell: ({row}) => formatEventStatus(row.original.status),
      },
      {
        accessorKey: 'startDate',
        header: 'Start date',
        Cell: ({row}) => formatInTz(row.original.startDate, DATETIME_FORMAT),
        size: 160,
      },
      {
        accessorKey: 'endDate',
        header: 'End date',
        Cell: ({row}) => formatInTz(row.original.endDate, DATETIME_FORMAT),
        size: 160,
      },
      {
        accessorKey: 'yesPrice',
        header: 'Yes price',
        Cell: ({row}) => {
          return <CurrencyValue value={row.original.yesPrice} />;
        },
        maxSize: 160,
        ...rightAlignmentColumnProps,
      },
      {
        accessorKey: 'noPrice',
        header: 'No price',
        Cell: ({row}) => {
          return <CurrencyValue value={row.original.noPrice} />;
        },
        maxSize: 160,
        ...rightAlignmentColumnProps,
      },
    ];
  }, [
    areAllCurrentPageEventsSelected,
    areSomeCurrentPageEventsSelected,
    currentPageEventSlugs.length,
    selectedEventSlugs,
    toggleCurrentPageSelection,
    toggleEventSelection,
    enqueueSnackbar,
  ]);

  if (!topic_id) {
    return 'Invalid topic ID';
  }

  const informationRoute = ROUTE__TOPIC_DETAILS.replace(':topic_id', topic_id);
  const chatRoute = ROUTE__TOPIC_CHAT.replace(':topic_id', topic_id);
  const poolsRoute = ROUTE__TOPIC_POOLS.replace(':topic_id', topic_id);
  const isPoolsTab = pathname === poolsRoute;
  const isChatTab = pathname === chatRoute;
  const tabs = [
    {route: informationRoute, label: 'Information'},
    {route: chatRoute, label: 'Chat'},
    {route: poolsRoute, label: 'Pools'},
  ];

  return (
    <>
      <Header title={`Topic ${topic_id}`} mb={1} />

      <BaseWidgetLayout isPending={isPending} error={error}>
        {data && (
          <>
            <CardBox bgcolor={COLOR__WHITE} mb={3}>
              <ClassicMuiNavTabs tabs={tabs} />
            </CardBox>

            {isChatTab ? (
              <CardBox bgcolor={COLOR__WHITE}>
                <ChatTab topicSlug={topic_id} />
              </CardBox>
            ) : isPoolsTab ? (
              <CardBox bgcolor={COLOR__WHITE}>
                <PoolsTable kind="topic" id={data.topicId} />
              </CardBox>
            ) : (
              <>
                <CardBox bgcolor={COLOR__WHITE} mb={3}>
                  <EditTopicForm
                    key={data.topicId}
                    topic={data}
                    onClose={handleClose}
                    onAutoPublishChange={handleAutoPublishChange}
                  />
                </CardBox>

                {isSportCategory && (
                  <>
                    <CardBox bgcolor={COLOR__WHITE} mb={3}>
                      <Header
                        title="Provider metadata"
                        mb={2}
                        isBackButtonShown={false}
                      />
                      {isSportPending ? (
                        <Typography color="text.secondary">
                          Loading...
                        </Typography>
                      ) : sportMatch ? (
                        (() => {
                          const locationId =
                            sportMatch.league.lsports?.location_id;
                          const competitionId = sportMatch.league.lsports?.id;
                          const eventId = sportMatch.lsports.id;
                          const lsportsBase =
                            'https://platform.lsports.eu/trade360/ordering/inplay?hierarchy=fixture';
                          const locationUrl = locationId
                            ? `${lsportsBase}&locationId=${locationId}`
                            : null;
                          const competitionUrl =
                            locationId && competitionId
                              ? `${lsportsBase}&locationId=${locationId}&competitionId=${competitionId}`
                              : null;
                          const eventUrl =
                            locationId && competitionId
                              ? `${lsportsBase}&locationId=${locationId}&competitionId=${competitionId}&search=${eventId}`
                              : null;
                          const logsUrl = `https://platform.lsports.eu/trade360/logs/inplay?search=${eventId}&curtainPropertyId=${eventId}&curtain-messageTypeText=1`;

                          const ExternalLinkButton = ({
                            href,
                          }: {
                            href: string | null;
                          }) =>
                            href ? (
                              <Tooltip title="Open in lsports">
                                <IconButton
                                  component="a"
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="small"
                                  sx={{ml: 0.5, verticalAlign: 'middle'}}
                                >
                                  <OpenInNewIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            ) : null;

                          return (
                            <Stack spacing={3}>
                              <Box>
                                <Box display="flex" alignItems="center" mb={1}>
                                  <Typography variant="h3bold">
                                    Event
                                  </Typography>
                                  <ExternalLinkButton href={eventUrl} />
                                  <Link
                                    href={logsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ml: 1}}
                                  >
                                    Logs
                                  </Link>
                                </Box>
                                <Stack spacing={0.5}>
                                  <Typography>
                                    <strong>ID:</strong> {eventId}
                                  </Typography>
                                  <Typography>
                                    <strong>Name:</strong>{' '}
                                    {sportMatch.lsports.name ?? '—'}
                                  </Typography>
                                  <Typography>
                                    <strong>Start date:</strong>{' '}
                                    {sportMatch.lsports.start_date
                                      ? formatInTz(
                                          new Date(
                                            sportMatch.lsports.start_date,
                                          ),
                                          DATETIME_FORMAT,
                                        )
                                      : '—'}
                                  </Typography>
                                  <Typography>
                                    <strong>Last update:</strong>{' '}
                                    {sportMatch.lsports.last_update
                                      ? formatInTz(
                                          new Date(
                                            sportMatch.lsports.last_update,
                                          ),
                                          DATETIME_FORMAT,
                                        )
                                      : '—'}
                                  </Typography>
                                  {isApiSportsMappingSupported && (
                                    <Box
                                      display="flex"
                                      alignItems="flex-end"
                                      gap={1}
                                    >
                                      <Box flex={1}>
                                        <ApiSportsMappingField
                                          kind="matches"
                                          categorySlug={sportKey}
                                          entityId={sportMatch.id}
                                          initial={sportMatch.apisports}
                                          label="APISports"
                                          season={selectedSeason ?? undefined}
                                        />
                                      </Box>
                                      <ApiSportsMatchField
                                        categorySlug={sportKey}
                                        entityId={sportMatch.id}
                                        initial={sportMatch.apisports}
                                        label="APISports (manual)"
                                      />
                                    </Box>
                                  )}
                                </Stack>
                              </Box>

                              {sportMatch.league.lsports && (
                                <>
                                  <Box>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      mb={1}
                                    >
                                      <Typography variant="h3bold">
                                        Location
                                      </Typography>
                                      <ExternalLinkButton href={locationUrl} />
                                    </Box>
                                    <Stack spacing={0.5}>
                                      <Typography>
                                        <strong>ID:</strong>{' '}
                                        {sportMatch.league.lsports.location_id}
                                      </Typography>
                                      <Typography>
                                        <strong>Name:</strong>{' '}
                                        {
                                          sportMatch.league.lsports
                                            .location_name
                                        }
                                      </Typography>
                                    </Stack>
                                  </Box>

                                  <Box>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      mb={1}
                                    >
                                      <Typography variant="h3bold">
                                        Competition
                                      </Typography>
                                      <ExternalLinkButton
                                        href={competitionUrl}
                                      />
                                    </Box>
                                    <Stack spacing={0.5}>
                                      <Typography>
                                        <strong>ID:</strong>{' '}
                                        {sportMatch.league.lsports.id}
                                      </Typography>
                                      <Typography>
                                        <strong>Name:</strong>{' '}
                                        {sportMatch.league.lsports.name}
                                      </Typography>
                                      {isApiSportsMappingSupported && (
                                        <>
                                          <Box>
                                            <ApiSportsMappingField
                                              kind="leagues"
                                              categorySlug={sportKey}
                                              entityId={sportMatch.league.id}
                                              initial={
                                                sportMatch.league.apisports
                                              }
                                              label="APISports"
                                            />
                                          </Box>
                                          {/* Padding rather than margin: the
                                      enclosing Stack zeroes margins on its
                                      direct children, so mt has no effect. */}
                                          <Box pt={4}>
                                            <SeasonSelect
                                              categorySlug={sportKey}
                                              leagueId={sportMatch.league.id}
                                              value={selectedSeason}
                                              onChange={setSelectedSeason}
                                            />
                                          </Box>
                                        </>
                                      )}
                                    </Stack>
                                  </Box>
                                </>
                              )}

                              {sportMatch.participants.length > 0 && (
                                <Box>
                                  <Typography
                                    variant="h3bold"
                                    display="block"
                                    mb={1}
                                  >
                                    Participants
                                  </Typography>
                                  <Stack spacing={2}>
                                    {[...sportMatch.participants]
                                      .filter(p => p.lsports)
                                      .sort(
                                        (a, b) =>
                                          a.lsports!.position -
                                          b.lsports!.position,
                                      )
                                      .map(p => (
                                        <Box key={p.id}>
                                          <Typography>
                                            <strong>
                                              #{p.lsports!.position}
                                            </strong>{' '}
                                            {p.lsports!.name} (ID:{' '}
                                            {p.lsports!.id})
                                          </Typography>
                                          {isApiSportsMappingSupported && (
                                            <ApiSportsMappingField
                                              kind="teams"
                                              categorySlug={sportKey}
                                              entityId={p.team_id}
                                              initial={p.apisports}
                                              label="APISports"
                                            />
                                          )}
                                        </Box>
                                      ))}
                                  </Stack>
                                </Box>
                              )}
                            </Stack>
                          );
                        })()
                      ) : (
                        <Typography color="text.secondary">
                          No match data found
                        </Typography>
                      )}
                    </CardBox>

                    {sportMatch && (
                      <CardBox bgcolor={COLOR__WHITE} mb={3}>
                        <Header
                          title="Participants"
                          mb={5}
                          isBackButtonShown={false}
                          rightPart={
                            <Button
                              variant="outlined"
                              disabled={isRefreshingTopic || !topic_id}
                              onClick={handleRefreshTopic}
                            >
                              Update topic data
                            </Button>
                          }
                          leftPart={
                            <Tooltip title="Create participant in new tab">
                              <IconButton
                                size="small"
                                component="a"
                                href={ROUTE__PARTICIPANT_CREATE}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          }
                        />
                        <Box display="flex" gap={3}>
                          {(
                            [
                              {
                                label: 'Participant 1',
                                value: participant1,
                                onChange: setParticipant1,
                              },
                              {
                                label: 'Participant 2',
                                value: participant2,
                                onChange: setParticipant2,
                              },
                            ] as const
                          ).map(({label, value, onChange}) => (
                            <Box key={label} flex={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box flex={1}>
                                  <ParticipantSelect
                                    label={label}
                                    value={value}
                                    onChange={onChange}
                                    categorySlug={sportKey}
                                  />
                                </Box>
                                <Tooltip
                                  title={
                                    value
                                      ? 'Open participant in new tab'
                                      : 'Select a participant first'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!value}
                                      component={value ? 'a' : 'button'}
                                      href={
                                        value
                                          ? ROUTE__PARTICIPANT_DETAILS.replace(
                                              ':participant_id',
                                              value.participantId,
                                            )
                                          : undefined
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                              {(value?.logoUrl || value?.logoUrlCustom) && (
                                <Box mt={1.5} display="flex" gap={1.5}>
                                  {(
                                    [
                                      {src: value.logoUrl, label: 'Logo'},
                                      {
                                        src: value.logoUrlCustom,
                                        label: 'Custom logo',
                                      },
                                    ] as const
                                  )
                                    .filter(logo => logo.src)
                                    .map(logo => (
                                      <Box key={logo.label}>
                                        <Typography
                                          color="text.secondary"
                                          sx={{fontSize: 12, mb: 0.5}}
                                        >
                                          {logo.label}
                                        </Typography>
                                        <Box
                                          width={80}
                                          height={80}
                                          border="1px solid #E0E0E0"
                                          borderRadius={1}
                                          bgcolor="#fafafa"
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                          overflow="hidden"
                                        >
                                          <Box
                                            component="img"
                                            src={logo.src!}
                                            alt={value.names[0]}
                                            sx={{
                                              width: '100%',
                                              height: '100%',
                                              objectFit: 'contain',
                                            }}
                                          />
                                        </Box>
                                      </Box>
                                    ))}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                        {hasParticipantsChanged && (
                          <Box mt={3}>
                            <Button
                              disabled={isLinking}
                              onClick={async () => {
                                const pairs = [
                                  {
                                    current: participant1,
                                    initial: initialParticipant1Ref.current,
                                    fp: sortedSportParticipants[0],
                                  },
                                  {
                                    current: participant2,
                                    initial: initialParticipant2Ref.current,
                                    fp: sortedSportParticipants[1],
                                  },
                                ].filter(
                                  ({current, fp}) => current && fp?.lsports,
                                );

                                try {
                                  await Promise.all(
                                    pairs.map(
                                      async ({current, initial, fp}) => {
                                        const subcategorySlug =
                                          data?.subCategories?.[0]?.slug;
                                        let uuid =
                                          await getExternalParticipantUuid(
                                            `lsports:${fp!.lsports!.id}`,
                                            subcategorySlug,
                                          );
                                        if (!uuid && fp!.id) {
                                          uuid =
                                            await getExternalParticipantUuid(
                                              `lsports:${fp!.id}`,
                                              subcategorySlug,
                                            );
                                        }
                                        if (!uuid) {
                                          throw new Error(
                                            `Could not resolve external UUID for participant ${fp!.lsports!.id}`,
                                          );
                                        }
                                        if (
                                          initial &&
                                          initial.participantId !==
                                            current!.participantId
                                        ) {
                                          await unbindExternalParticipant(
                                            initial.participantId,
                                            uuid,
                                          );
                                        }
                                        await bindExternalParticipant({
                                          participantId: current!.participantId,
                                          externalParticipantIds: [uuid],
                                        });
                                      },
                                    ),
                                  );
                                  const linkedIds = [
                                    participant1?.participantId,
                                    participant2?.participantId,
                                  ].filter((id): id is string => !!id);
                                  await updateTopicParticipants(
                                    topic_id,
                                    linkedIds,
                                  );
                                  await queryClient.invalidateQueries({
                                    queryKey: [QUERY__TOPIC, topic_id],
                                  });
                                  // Refs are set from the closed-over selected
                                  // participants, not from their own stale value.
                                  /* eslint-disable require-atomic-updates */
                                  initialParticipant1Ref.current = participant1;
                                  initialParticipant2Ref.current = participant2;
                                  /* eslint-enable require-atomic-updates */
                                  enqueueSnackbar(
                                    'Participants linked successfully',
                                    {variant: 'success'},
                                  );
                                } catch (err) {
                                  const errorMessage = isAxiosError(err)
                                    ? getErrorMessageFromApiError(err)
                                    : '';
                                  enqueueSnackbar(
                                    `Could not link participants. ${errorMessage}`,
                                    {variant: 'error'},
                                  );
                                }
                              }}
                            >
                              Link participants
                            </Button>
                          </Box>
                        )}
                      </CardBox>
                    )}
                  </>
                )}

                <CardBox bgcolor={COLOR__WHITE}>
                  <Header
                    title="Topic events"
                    mb={2}
                    isBackButtonShown={false}
                  />

                  <MrTable
                    columns={topicEventsColumns}
                    data={eventsData?.items ?? []}
                    error={eventsError}
                    renderTopToolbarCustomActions={() => {
                      return (
                        <Box
                          display="flex"
                          width="100%"
                          justifyContent="flex-start"
                          gap={1}
                        >
                          <Button
                            onClick={() => {
                              void handlePublishSelectedEvents();
                            }}
                            disabled={
                              selectedEventSlugs.size === 0 ||
                              selectedPublishedEventSlugs.size > 0 ||
                              isPublishingEvents ||
                              isConfirmingEvents
                            }
                          >
                            Publish
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setIsGroupDialogOpen(true);
                            }}
                            disabled={selectedEventSlugs.size === 0}
                          >
                            Group
                          </Button>
                        </Box>
                      );
                    }}
                    enablePagination
                    manualPagination
                    rowCount={eventsData?.total ?? 0}
                    onPaginationChange={setUrl}
                    state={{
                      isLoading: isEventsPending,
                      pagination: urlState,
                    }}
                    {...entityTableCommonProps}
                    enableRowActions={false}
                  />
                </CardBox>
              </>
            )}
          </>
        )}
      </BaseWidgetLayout>

      <CreateEventGroupDialog
        open={isGroupDialogOpen}
        onClose={() => setIsGroupDialogOpen(false)}
        onCreate={groupType => {
          void handleCreateGroup(groupType);
        }}
        isCreating={isCreatingGroup}
      />
    </>
  );
};
